class SpecsSectionV2 extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    
    // Default values
    this.label = this.getAttribute('label') || 'Technical Specifications';
    this.specsData = this.parseSpecsData();
    this.isOpen = false;
    this.originalScrollY = 0;
    this.originalPaddingRight = '';
    
    this.render();
    this.setupEventListeners();
  }
  
  parseSpecsData() {
    const specsAttr = this.getAttribute('data-specs');
    if (!specsAttr) return [];
    try {
      const parsed = JSON.parse(specsAttr);
      console.log('Successfully parsed specs data:', parsed);
      return parsed;
    } catch (e) {
      console.error('Failed to parse specs data:', e);
      console.error('Raw data-specs value:', specsAttr);
      return [];
    }
  }
  
  groupSpecsByGroup() {
    const groups = {};
    this.specsData.forEach(spec => {
      const groupName = spec.group || 'General';
      if (!groups[groupName]) {
        groups[groupName] = [];
      }
      groups[groupName].push({ title: spec.title, value: spec.value });
    });
    return groups;
  }
  
  renderSpecsGroups() {
    const groups = this.groupSpecsByGroup();
    const groupNames = Object.keys(groups);
    
    if (groupNames.length === 0) {
      return '<p>No specifications available.</p>';
    }
    
    return groupNames.map((groupName, index) => {
      const specs = groups[groupName].filter(spec => spec.value && spec.value.trim() !== '');
      
      if (specs.length === 0) {
        return '';
      }
      
      const isFirstGroup = index === 0;
      
      return `
        <div class="specs-group">
          <div class="specs-group-header">
            <span class="specs-group-title">${groupName}</span>
            <svg class="specs-group-arrow${isFirstGroup ? ' expanded' : ''}" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <polyline points="6 9 12 15 18 9"/>
            </svg>
          </div>
          <div class="specs-group-content${isFirstGroup ? ' expanded' : ''}">
            <div class="specs-grid">
              ${specs.map(spec => `
                <div class="specs-item">
                  <span class="specs-item-label">${spec.title}</span>
                  <span class="specs-item-value">${spec.value}</span>
                </div>
              `).join('')}
            </div>
          </div>
        </div>
      `;
    }).join('');
  }
  
  handleSpecsGroupClick(e) {
    const header = e.target.closest('.specs-group-header');
    if (header) {
      const group = header.closest('.specs-group');
      const content = group.querySelector('.specs-group-content');
      const arrow = header.querySelector('.specs-group-arrow');
      
      if (content && arrow) {
        content.classList.toggle('expanded');
        arrow.classList.toggle('expanded');
      }
    }
  }
  
  static get observedAttributes() {
    return ['label', 'data-specs'];
  }
  
  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue !== newValue) {
      switch (name) {
        case 'label':
          this.label = newValue;
          this.render();
          this.setupEventListeners();
          break;
        case 'data-specs':
          this.specsData = this.parseSpecsData();
          this.render();
          this.setupEventListeners();
          break;
      }
    }
  }
  
  render() {
    this.shadowRoot.innerHTML = `
      <style>
        .specs-section {
          margin: 20px 0;
        }
        
        .specs-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 16px 12px;
          border-bottom: 1px solid #e0e0e0;
          cursor: pointer;
          transition: all 0.2s ease;
        }
        
        .specs-header:hover {
          border-color: #000;
        }
        
        .specs-label {
          font-weight: 600;
        }
        
        .specs-arrow {
          transition: transform 0.3s ease;
        }
        
        .specs-drawer {
          position: fixed;
          top: 82.5px;
          right: -100%;
          width: 100%;
          max-width: 500px;
          height: calc(100vh - 82.5px);
          background: #fff;
          box-shadow: -2px 0 10px rgba(0, 0, 0, 0.1);
          transition: right 0.3s ease;
          z-index: 1000;
          display: flex;
          flex-direction: column;
        }
        
        .specs-drawer.open {
          right: 0;
          z-index: 9999;
        }
        
        .specs-drawer-content {
          flex: 1;
          display: flex;
          flex-direction: column;
          min-height: 0;
        }
        
        .specs-drawer-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 20px;
          border-bottom: 1px solid #e0e0e0;
          color: #000;
          flex-shrink: 0;
        }
        
        .specs-drawer-header h3 {
          margin: 0;
          font-size: 18px;
          font-weight: 600;
        }
        
        .specs-drawer-close {
          background: none;
          border: none;
          cursor: pointer;
          padding: 5px;
        }
        
        .specs-drawer-body {
          flex: 1;
          padding: 20px;
          overflow-y: auto;
          overflow-x: hidden;
          min-height: 0;
        }
        
        .specs-drawer-body::-webkit-scrollbar {
          width: 8px;
        }
        
        .specs-drawer-body::-webkit-scrollbar-track {
          background: #f1f1f1;
        }
        
        .specs-drawer-body::-webkit-scrollbar-thumb {
          background: #888;
          border-radius: 4px;
        }
        
        .specs-drawer-body::-webkit-scrollbar-thumb:hover {
          background: #555;
        }
        
        .specs-drawer-overlay {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: rgba(0, 0, 0, 0.5);
          opacity: 0;
          visibility: hidden;
          transition: all 0.3s ease;
          z-index: 999;
        }
        
        .specs-drawer-overlay.visible {
          opacity: 1;
          visibility: visible;
        }
        
        .specs-group {
          margin-bottom: 20px;
          overflow: hidden;
        }
        
        .specs-group-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          border-bottom: 1px solid #e0e0e0;
          padding: 12px 16px;
          cursor: pointer;
          transition: background 0.2s ease;
        }
        
        .specs-group-header:hover {
          background: #ebebeb;
        }
        
        .specs-group-title {
          color: #000;
          font-weight: 600;
          font-size: 18px;
        }
        
        .specs-group-arrow {
          transition: transform 0.3s ease;
          color: #000;
        }
        
        .specs-group-arrow.expanded {
          transform: rotate(180deg);
        }
        
        .specs-group-content {
          max-height: 0;
          overflow: hidden;
          transition: max-height 0.3s ease;
        }
        
        .specs-group-content.expanded {
          max-height: 1000px;
          margin-top: -1px;
          border-top: 1px solid #000;
        }
        
        .specs-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 0;
        }
        
        .specs-item {
          display: flex;
          flex-direction: column;
          padding: 10px 16px;
          border-bottom: 1px solid #f0f0f0;
        }
        
        .specs-item:last-child {
          border-bottom: none;
        }
        
        .specs-item-label {
          color: #000;
          font-weight: 700;
          font-size: 16px;
        }
        
        .specs-item-value {
          color: #000;
          font-size: 16px;
        }
        
        @media (max-width: 768px) {
          .specs-drawer {
            top: 72px;
            height: calc(100vh - 72px);
          }
        }
      </style>
      <div class="specs-section">
        <div class="specs-header" id="specs-header">
          <span class="specs-label">${this.label}</span>
          <svg class="specs-arrow" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <polyline points="9 18 15 12 9 6"/>
          </svg>
        </div>
      </div>
      
      <!-- Technical Specifications Drawer -->
      <div class="specs-drawer" id="specs-drawer">
        <div class="specs-drawer-content">
          <div class="specs-drawer-header">
            <h3>${this.label}</h3>
            <button class="specs-drawer-close" id="specs-drawer-close">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <line x1="18" y1="6" x2="6" y2="18"/>
                <line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
          </div>
          <div class="specs-drawer-body" id="specs-drawer-body">
            ${this.renderSpecsGroups()}
          </div>
        </div>
      </div>
      <div class="specs-drawer-overlay" id="specs-drawer-overlay"></div>
    `;
  }
  
  setupEventListeners() {
    const specsHeader = this.shadowRoot.getElementById('specs-header');
    const specsDrawer = this.shadowRoot.getElementById('specs-drawer');
    const specsDrawerOverlay = this.shadowRoot.getElementById('specs-drawer-overlay');
    const specsDrawerClose = this.shadowRoot.getElementById('specs-drawer-close');
    const specsDrawerBody = this.shadowRoot.getElementById('specs-drawer-body');
    
    if (specsHeader) {
      specsHeader.addEventListener('click', () => {
        this.openDrawer();
      });
    }
    
    if (specsDrawerClose) {
      specsDrawerClose.addEventListener('click', () => {
        this.closeDrawer();
      });
    }
    
    if (specsDrawerOverlay) {
      specsDrawerOverlay.addEventListener('click', () => {
        this.closeDrawer();
      });
    }
    
    if (specsDrawerBody) {
      specsDrawerBody.addEventListener('click', (e) => {
        this.handleSpecsGroupClick(e);
      });
    }
    
    // Close drawer on ESC key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) {
        this.closeDrawer();
      }
    });
  }
  
  openDrawer() {
    const specsDrawer = this.shadowRoot.getElementById('specs-drawer');
    const specsDrawerOverlay = this.shadowRoot.getElementById('specs-drawer-overlay');
    
    if (specsDrawer && specsDrawerOverlay) {
      // Save original scroll position and body style
      this.originalScrollY = window.scrollY;
      this.originalPaddingRight = document.body.style.paddingRight || '';
      
      // Calculate scrollbar width
      const scrollbarWidth = this.getScrollbarWidth();
      
      // Prevent body scroll
      document.body.style.position = 'fixed';
      document.body.style.top = `-${this.originalScrollY}px`;
      document.body.style.right = '0';
      document.body.style.left = '0';
      document.body.style.paddingRight = `${scrollbarWidth}px`;
      
      // Open drawer
      specsDrawer.classList.add('open');
      specsDrawerOverlay.classList.add('visible');
      
      this.isOpen = true;
      
      // Dispatch event
      this.dispatchEvent(new CustomEvent('drawer-open'));
    }
  }
  
  closeDrawer() {
    const specsDrawer = this.shadowRoot.getElementById('specs-drawer');
    const specsDrawerOverlay = this.shadowRoot.getElementById('specs-drawer-overlay');
    
    if (specsDrawer && specsDrawerOverlay) {
      // Close drawer
      specsDrawer.classList.remove('open');
      specsDrawerOverlay.classList.remove('visible');
      
      // Restore body style and scroll position
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.right = '';
      document.body.style.left = '';
      document.body.style.paddingRight = this.originalPaddingRight;
      
      // Restore scroll position
      window.scrollTo({ top: this.originalScrollY, behavior: 'instant' });
      
      this.isOpen = false;
      
      // Dispatch event
      this.dispatchEvent(new CustomEvent('drawer-close'));
    }
  }
  

  
  getScrollbarWidth() {
    const scrollDiv = document.createElement('div');
    scrollDiv.style.cssText = 'width: 100px; height: 100px; overflow: scroll; position: absolute; top: -9999px;';
    document.body.appendChild(scrollDiv);
    const scrollbarWidth = scrollDiv.offsetWidth - scrollDiv.clientWidth;
    document.body.removeChild(scrollDiv);
    return scrollbarWidth;
  }
  
  // Public method to check if drawer is open
  isDrawerOpen() {
    return this.isOpen;
  }
  
  // Public method to toggle drawer
  toggleDrawer() {
    if (this.isOpen) {
      this.closeDrawer();
    } else {
      this.openDrawer();
    }
  }
}

customElements.define('specs-section-v2', SpecsSectionV2);